import classNames from 'classnames/bind'
import { useTranslation } from 'react-i18next'

import type { FormQuestion } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import {
  ButtonText,
  Dropzone,
  Flex,
  FormField,
  MultiSelect,
  Select,
  TextArea,
  TextInput,
  Typography
} from '@percent/lemonade'
import styles from './PreviewQuestion.module.scss'

const cx = classNames.bind(styles)

export type PreviewQuestionProps = {
  question: FormQuestion
}

export function PreviewQuestion({ question }: PreviewQuestionProps) {
  const { t } = useTranslation()
  const { id, type, required, label, description, instructions, placeholder } = question

  const selectOptions =
    question.options?.map(option => ({
      label: option.label,
      value: option.value
    })) ?? []

  const renderQuestionInput = () => {
    switch (type) {
      case 'textarea':
        return (
          <Flex direction="column" w="100%">
            <TextArea defaultValue="" name={id} placeholder={placeholder ?? ''} />
          </Flex>
        )

      case 'score':
        return (
          <Flex direction="column" gap={8} flex={1}>
            <Flex gap={8} justify="space-between">
              {Array.from({ length: question.maxScore || 5 }, (_, i) => (
                <ButtonText key={`${id}-${i + 1}`} className={cx('scoreButton')}>
                  {i + 1}
                </ButtonText>
              ))}
            </Flex>
            {question.maxScore && (
              <Typography variant="small" color="gray300">
                {t('grants.programDetails.taskTemplates.preview.maxScore')}: {question.maxScore}
              </Typography>
            )}
          </Flex>
        )

      case 'award_amount':
      case 'final_award_amount':
        return (
          <Flex direction="column" w="100%">
            <TextInput
              defaultValue=""
              name={id}
              type="number"
              placeholder={t('grants.programDetails.taskTemplates.preview.amountPlaceholder')}
            />
          </Flex>
        )

      case 'number':
        return (
          <Flex direction="column" w="100%">
            <TextInput
              defaultValue=""
              name={id}
              type="number"
              placeholder={placeholder ?? t('grants.programDetails.taskTemplates.preview.numberPlaceholder')}
            />
          </Flex>
        )

      case 'select':
        if (question.multiple) {
          return (
            <Flex direction="column" w="100%">
              <MultiSelect
                name={id}
                placeholder={t('grants.programDetails.taskTemplates.preview.selectPlaceholder')}
                options={selectOptions}
              />
            </Flex>
          )
        }

        return (
          <Flex direction="column" w="100%">
            <Select
              name={id}
              placeholder={t('grants.programDetails.taskTemplates.preview.selectPlaceholder')}
              options={selectOptions}
            />
          </Flex>
        )
      case 'award_recommendation':
      case 'organisation_search':
      case 'country_search':
      case 'registry_search':
        return (
          <Flex direction="column" w="100%">
            <Select
              name={id}
              placeholder={t('grants.programDetails.taskTemplates.preview.selectPlaceholder')}
              options={selectOptions}
            />
          </Flex>
        )

      case 'radio':
      case 'checkbox':
        return (
          <Flex direction="column" gap={8}>
            {question.options?.map(option => (
              <Flex key={option.value} align="center" gap={8}>
                <input type={type} name={id} />
                <Typography variant="default">{option.label}</Typography>
              </Flex>
            ))}
          </Flex>
        )

      case 'documents':
        return (
          <Flex direction="column" w="100%">
            <Dropzone
              title={t('grants.programDetails.taskTemplates.preview.dropzoneTitle')}
              description={t('grants.programDetails.taskTemplates.preview.dropzoneDescription')}
              files={[]}
              onChange={() => {}}
            />
          </Flex>
        )

      case 'decision':
        return (
          <Flex gap={8} flex={1}>
            {question.options?.map(option => {
              const optionKey = option.key

              return (
                <ButtonText key={optionKey} className={cx('decisionButton', optionKey)}>
                  {option.label}
                </ButtonText>
              )
            })}
          </Flex>
        )

      default:
        return (
          <Flex direction="column" w="100%">
            <TextInput
              defaultValue=""
              name={id}
              placeholder={t('grants.programDetails.taskTemplates.preview.textPlaceholder')}
            />
          </Flex>
        )
    }
  }

  return (
    <Flex direction="column" gap={16}>
      <FormField
        label={label}
        necessity={required ? 'required' : 'optional'}
        description={description}
        instructions={instructions}
      >
        <Flex flex={1} w="100%">
          {renderQuestionInput()}
        </Flex>
      </FormField>
    </Flex>
  )
}
